/**
 * Save a class video to the viewer's device.
 *
 * Bobbie, 2026-08-28: *"The other thing we need to add to all video is a
 * download button. Parents are asking to be able to save the videos which we
 * are ok with."*
 *
 * lib/videoDownload.ts decides WHETHER and BY WHICH ROUTE (pure, tested); this
 * file performs it. There are two places a video can live and two things a
 * device can do with it:
 *
 *   R2, NOT AN iPHONE -> the server mints a one-shot presigned URL carrying
 *   `Content-Disposition: attachment`, and the browser's own download
 *   machinery streams it straight to disk. Nothing passes through this app's
 *   memory, so a full-length recital video is no harder than a ten-second
 *   clip. One tap. The presign re-proves the caller can currently see this
 *   class's media, exactly as the playback presign does: saving a video is
 *   not a wider permission than watching it, and this does not make it one.
 *
 *   R2 ON AN iPHONE OR iPAD (feedback UrvXulJH, Amanda, 2026-09-17, "no
 *   download") -> the same presign, then the BYTES are fetched from that URL
 *   and handed to the share sheet, because the share sheet is the only road a
 *   web app has to Photos and it needs the file in hand. An attachment URL on
 *   its own never reaches it: this route used to click that URL and report
 *   "Saved" while the phone never offered Save Video at all. Bounded by
 *   VIDEO_FETCH_MAX_BYTES like the Firebase route below. If the bytes cannot
 *   be fetched (no network, or the bucket's CORS policy does not allow a
 *   browser GET, docs/R2-GO-LIVE-STEPS.md Step 1) or the file is over the
 *   budget, it falls back to the attachment download it used to do, and says
 *   plainly that the video will not go to Photos. It never says Saved for that.
 *
 *   FIREBASE STORAGE, NOT AN iPHONE (Bobbie, 2026-10-23, "no one can save
 *   videos ... it has a save video, but doesnt save") -> the studio signs a
 *   short-lived URL for the exact object with `Content-Disposition:
 *   attachment` (netlify/functions/attachmentDownload.mjs), and the browser
 *   downloads it the way it downloads an R2 video. This used to fetch() the
 *   bytes from the page, which never worked: the bucket sends no CORS
 *   header, so the fetch fails while a <video> on the same URL plays.
 *
 *   FIREBASE STORAGE ON AN iPHONE OR iPAD -> fetch the bytes for the share
 *   sheet (they arrive once the bucket allows the Hub's origin, HUB-SETUP.md
 *   Phase 1), then the shared device ladder in lib/saveAttachment.ts. If the
 *   bytes cannot be fetched or are over the budget, the same signed download
 *   as above, with the honest "will not go to Photos" reason, never Saved.
 *
 *   Either Firebase route without a signer (an older caller, or a test), or
 *   when the studio cannot sign: the bytes route, and its own sentences.
 *
 * Every failure ends in a sentence the button can show. Nothing here ever
 * reports a save that did not happen.
 *
 * NODE-LOADABLE ON PURPOSE. Every import below names its `.ts` file and none
 * of them reaches Firebase or React, so tests/video-save-ios-r2.test.mjs runs
 * THIS file, not a copy of it. That is why the presign is passed in by the
 * caller (SaveVideoButton binds lib/videoPlayback.ts's requestR2DownloadUrl)
 * instead of being imported here.
 */
import {
  deliverBlobToDevice,
  isIosDevice,
  type BlobDeliveryOptions,
  type SaveBlobOutcome,
} from './saveAttachment.ts';
import { isPreparedDownloadUrlFresh, type PreparedDownloadUrl } from './saveLifecycle.ts';
import {
  videoDownloadPlan,
  videoFetchDecision,
  type DownloadableLink,
} from './videoDownload.ts';

export type SaveVideoOutcome = SaveBlobOutcome;

/**
 * Why an iPhone save of an R2 video went to the browser's download instead
 * of the share sheet. The button turns each into one honest sentence.
 *   'too-big'     the file is over VIDEO_FETCH_MAX_BYTES, so its bytes were
 *                 never pulled into the phone's memory.
 *   'unreachable' the bytes could not be fetched for any other reason.
 */
export type ShareSheetFallback = 'too-big' | 'unreachable';

export type PreparedVideoSave =
  | {
    route: 'r2';
    url: string;
    preparedAtMs: number;
    validForMs: number;
    fileName: string;
    /** Set only on an iPhone or iPad, when the share sheet could not be used. */
    shareSheetFallback?: ShareSheetFallback;
  }
  | {
    /** A signed Firebase Storage URL that answers with Content-Disposition. */
    route: 'link';
    url: string;
    preparedAtMs: number;
    validForMs: number;
    fileName: string;
    /** Set only on an iPhone or iPad, when the share sheet could not be used. */
    shareSheetFallback?: ShareSheetFallback;
  }
  | { route: 'blob'; blob: Blob; fileName: string; contentType?: string };

/** The one prepared value that may wait for a fresh tap: bytes in hand. */
export type PreparedVideoBlob = Extract<PreparedVideoSave, { route: 'blob' }>;

/**
 * Thrown with a sentence meant for a parent's eyes. Every message here is
 * something a person can act on; none of them is a status code.
 */
export class SaveVideoError extends Error {}

/** The fetch was refused by VIDEO_FETCH_MAX_BYTES, not by the network. */
export class VideoOverBudgetError extends Error {}

export interface SaveVideoInput {
  link: DownloadableLink;
  /** The filename to save as. Build it with videoFileName(). */
  fileName: string;
  /**
   * The type the STUDIO recorded for this file. Used when the fetched bytes
   * carry no media type of their own; without it such a file reaches the iOS
   * share sheet as application/octet-stream and loses the "Save Video" option.
   */
  contentType?: string | null;
  /**
   * Mints the one-shot R2 download URL for this link's r2Key. The caller
   * binds who is asking (SaveVideoButton passes requestR2DownloadUrl with the
   * signed-in user or the dancer session).
   */
  presignR2Download: (r2Key: string) => Promise<PreparedDownloadUrl>;
  /**
   * Signs a `Content-Disposition: attachment` URL for a Firebase Storage
   * link (lib/attachmentDownloadLink.ts's requestAttachmentDownloadUrl).
   * Optional: without it a Firebase link takes the bytes route only.
   */
  signFirebaseDownload?: (url: string, fileName: string) => Promise<PreparedDownloadUrl>;
  /** Tests only. Defaults to the real device check in lib/saveAttachment.ts. */
  isIos?: () => boolean;
  /** Tests only. Defaults to the global fetch. */
  fetchImpl?: (url: string) => Promise<Response>;
}

const MEDIA_TYPE_BY_EXTENSION: Record<string, string> = {
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  m4v: 'video/x-m4v',
  webm: 'video/webm',
  '3gp': 'video/3gpp',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  aac: 'audio/aac',
  ogg: 'audio/ogg',
  flac: 'audio/flac',
  aif: 'audio/aiff',
  aiff: 'audio/aiff',
};

function isMediaType(type: string | null | undefined): type is string {
  const clean = (type ?? '').toLowerCase();
  return clean.startsWith('video/') || clean.startsWith('audio/');
}

/**
 * The type the share sheet will see. iOS offers Save Video only for a file
 * whose type says video, so a stored object served as
 * application/octet-stream is retyped from what the studio recorded, then
 * from the saved name's own extension. Re-wrapping a Blob does not copy its
 * bytes.
 */
export function withShareableType(blob: Blob, declared: string | null | undefined, fileName: string): Blob {
  if (isMediaType(blob.type)) return blob;
  const extension = (/\.([a-z0-9]{2,4})$/i.exec(fileName)?.[1] ?? '').toLowerCase();
  const type = isMediaType(declared) ? declared : MEDIA_TYPE_BY_EXTENSION[extension];
  return type ? new Blob([blob], { type }) : blob;
}

/** The budget's own refusal for this many bytes, or null when it fits. */
function overBudget(sizeBytes: number | null): VideoOverBudgetError | null {
  const decision = videoFetchDecision(sizeBytes);
  return decision.action === 'skip' ? new VideoOverBudgetError(decision.reason) : null;
}

/**
 * Read a response body into a Blob, refusing once it passes the budget.
 * Used only when the size was not declared up front, so an undeclared
 * 6 GB original is stopped at VIDEO_FETCH_MAX_BYTES instead of filling the
 * phone's memory first.
 */
async function readBodyWithinBudget(body: ReadableStream<Uint8Array>, type: string): Promise<Blob> {
  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.byteLength;
    const refused = overBudget(total);
    if (refused) {
      await reader.cancel().catch(() => undefined);
      throw refused;
    }
    chunks.push(value);
  }
  return new Blob(chunks as BlobPart[], { type });
}

/**
 * Fetch a video's bytes under VIDEO_FETCH_MAX_BYTES, checked against the
 * declared Content-Length before buffering and against the real byte count
 * after. A refused size throws VideoOverBudgetError; anything else that goes
 * wrong throws whatever the network threw.
 */
export async function fetchVideoWithinBudget(
  url: string,
  fetchImpl: (url: string) => Promise<Response> = (target) => fetch(target),
): Promise<Blob> {
  const response = await fetchImpl(url);
  if (!response.ok) throw new Error(`fetch failed (${response.status})`);
  const declared = Number(response.headers.get('content-length'));
  const declaredBytes = Number.isFinite(declared) && declared > 0 ? declared : null;
  const before = overBudget(declaredBytes);
  if (before) {
    // Stop the transfer rather than leave a 6 GB body draining in the
    // background after the parent has already been told no.
    await response.body?.cancel().catch(() => undefined);
    throw before;
  }
  const blob = declaredBytes == null && response.body
    ? await readBodyWithinBudget(response.body, response.headers.get('content-type') ?? '')
    : await response.blob();
  // The declared length can be absent or wrong; the real size cannot be.
  const after = overBudget(blob.size);
  if (after) throw after;
  return blob;
}

/**
 * Do the network half of a save without consuming the result. The caller
 * finishes it at once; only a blob whose share was refused for an expired
 * tap is kept for one fresh tap (see SaveVideoButton).
 */
export async function prepareVideoForDevice(input: SaveVideoInput): Promise<PreparedVideoSave> {
  const plan = videoDownloadPlan(input.link);
  if (plan.route === 'none') throw new SaveVideoError(plan.reason);
  const fetchImpl = input.fetchImpl ?? ((target: string) => fetch(target));

  if (plan.route === 'r2') {
    let signed: PreparedDownloadUrl;
    try {
      signed = await input.presignR2Download(plan.r2Key);
    } catch (error) {
      throw new SaveVideoError(
        error instanceof Error && error.message
          ? error.message
          : 'This file could not be saved right now. Try again in a moment.',
      );
    }
    const download: PreparedVideoSave = {
      route: 'r2',
      url: signed.url,
      preparedAtMs: signed.preparedAtMs,
      validForMs: signed.validForMs,
      fileName: input.fileName,
    };
    // Off an iPhone or iPad the attachment URL is the right tool: one tap,
    // straight to disk, nothing held in memory.
    if (!(input.isIos ?? isIosDevice)()) return download;

    // On an iPhone or iPad the share sheet is the only way to Photos, and it
    // needs the file itself.
    try {
      const blob = await fetchVideoWithinBudget(signed.url, fetchImpl);
      return {
        route: 'blob',
        blob: withShareableType(blob, input.contentType, input.fileName),
        fileName: input.fileName,
        ...(input.contentType ? { contentType: input.contentType } : {}),
      };
    } catch (error) {
      // Today's download, with the reason the share sheet was not used, so
      // the button can say the video will not go to Photos. Never Saved.
      return {
        ...download,
        shareSheetFallback: error instanceof VideoOverBudgetError ? 'too-big' : 'unreachable',
      };
    }
  }

  const ios = (input.isIos ?? isIosDevice)();
  const signedLink = async (shareSheetFallback?: ShareSheetFallback): Promise<PreparedVideoSave | null> => {
    if (!input.signFirebaseDownload) return null;
    try {
      const signed = await input.signFirebaseDownload(plan.url, input.fileName);
      return {
        route: 'link',
        url: signed.url,
        preparedAtMs: signed.preparedAtMs,
        validForMs: signed.validForMs,
        fileName: input.fileName,
        ...(shareSheetFallback ? { shareSheetFallback } : {}),
      };
    } catch (error) {
      // The studio is busy (many families on one wifi): its own sentence,
      // never the bytes route the bucket refuses.
      if ((error as { code?: string } | null)?.code === 'too_many_requests') {
        throw new SaveVideoError((error as Error).message);
      }
      return null;
    }
  };

  // Off an iPhone or iPad the signed link is the right tool: one tap,
  // straight to disk, no CORS, nothing held in memory.
  if (!ios) {
    const viaLink = await signedLink();
    if (viaLink) return viaLink;
  }

  let blob: Blob;
  try {
    blob = await fetchVideoWithinBudget(plan.url, fetchImpl);
  } catch (error) {
    // On an iPhone the bytes were for the share sheet. Without them, the
    // signed download still saves the file, and says it will not reach
    // Photos.
    if (ios) {
      const viaLink = await signedLink(error instanceof VideoOverBudgetError ? 'too-big' : 'unreachable');
      if (viaLink) return viaLink;
    }
    // A budget refusal is a decision, not a network failure: it keeps its
    // own sentence rather than wearing the connection one.
    if (error instanceof VideoOverBudgetError) throw new SaveVideoError(error.message);
    throw new SaveVideoError(
      'This file could not be saved here. Try Save again, or open the original and save it from there.',
    );
  }

  return {
    route: 'blob',
    blob: withShareableType(blob, input.contentType, input.fileName),
    fileName: input.fileName,
    ...(input.contentType ? { contentType: input.contentType } : {}),
  };
}

/**
 * Finish a prepared save. When the first share was refused because the tap
 * had expired, the caller passes the retained blob back in here as the first
 * act of the next tap: no network await happens before share()/click() on
 * this path.
 */
export async function finishPreparedVideoSave(
  prepared: PreparedVideoSave,
  options: BlobDeliveryOptions = {},
): Promise<SaveVideoOutcome> {
  if (prepared.route === 'blob') {
    return deliverBlobToDevice(
      prepared.blob,
      prepared.fileName,
      prepared.contentType,
      options,
    );
  }

  if (!isPreparedDownloadUrlFresh(prepared.preparedAtMs, prepared.validForMs)) {
    throw new SaveVideoError('That save link expired. Tap Save again to make a fresh one.');
  }

  // The URL answers with `Content-Disposition: attachment` (R2's presign, or
  // the signed Firebase link), so navigating to it saves rather than plays,
  // cross-origin, with no bytes buffered here. `download` is set too:
  // browsers may ignore it cross-origin, but the response header remains
  // authoritative. Nothing here can see whether the
  // browser then finished the download, so the button says Download started,
  // never Saved.
  const anchor = document.createElement('a');
  anchor.href = prepared.url;
  anchor.download = prepared.fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  return 'downloaded';
}
